import {
  AutoGraph,
  AdminPanelSettings,
  DarkMode,
  EventRepeat,
  Forum,
  Group,
  Insights,
  Inventory2,
  LightMode,
  Logout,
  Menu as MenuIcon,
  Psychology,
  RequestQuote,
  Settings,
  Luggage,
  DirectionsCar,
  Cabin,
  LocationOn,
  WorkspacePremium,
  WhatsApp,
} from "@mui/icons-material";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { Link, Outlet } from "react-router-dom";
import ConfirmationDialog from "../components/dialogs/ConfirmationDialog";
import { drawerWidth } from "../config/config";
import { useColorMode } from "../data/providers/AppThemeProvider";
import { platformTool } from "../utils/PlatformTool";
import { useDashboard } from "./hooks/useDashboard";

type DashboardMenuItem = {
  text: string;
  icon: React.ReactElement<any>;
  path: string;
  accessPath?: string;
  activePaths?: string[];
};

type DashboardMenuGroup = {
  title: string;
  items: DashboardMenuItem[];
};

const LYPRO_SUPPORT_WHATSAPP =
  process.env.REACT_APP_LYPRO_SUPPORT_WHATSAPP || "573045643425";

const getSupportWhatsAppUrl = (fullName?: string) => {
  const message = encodeURIComponent(
    `Hola LYPRO, necesito ayuda con mi dashboard.${fullName ? ` Soy ${fullName}.` : ""}`
  );
  return `https://wa.me/${LYPRO_SUPPORT_WHATSAPP}?text=${message}`;
};

const formatRoleLabel = (role?: string) => {
  const normalizedRole = String(role || "").trim();
  if (!normalizedRole) return "Usuario";

  const roleLabels: Record<string, string> = {
    ADM: "Admin",
    ADMIN: "Admin",
    OWNER: "Propietario",
    AGENT: "Agente",
    USER: "Usuario",
  };
  return roleLabels[normalizedRole.toUpperCase()] || normalizedRole;
};

const Dashboard: React.FC = () => {
  const {
    user,
    userData,
    soulLoading,
    permittedPaths,
    isMobile,
    mobileOpen,
    dialogOpen,
    location,
    theme,
    open,
    anchorEl,
    notifications,
    handleClose,
    setMobileOpen,
    setDialogOpen,
    handleDrawerToggle,
    handleLogout,
  } = useDashboard();
  const { isDarkMode, toggleColorMode } = useColorMode();
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const profileMenuOpen = Boolean(profileMenuAnchorEl);
  const appVersion = React.useMemo(() => platformTool.getAppVersion(), []);
  const roleLabel = formatRoleLabel(userData?.role);
  const supportWhatsAppUrl = getSupportWhatsAppUrl(userData?.fullName);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchorEl(null);
  };

  const handleThemeMenuClick = () => {
    toggleColorMode();
    handleProfileMenuClose();
  };

  const handleLogoutMenuClick = () => {
    handleProfileMenuClose();
    setDialogOpen(true);
  };

  const menuGroups: DashboardMenuGroup[] = [
    {
      title: "Analíticas",
      items: [
        { text: "Resumen", icon: <AutoGraph />, path: "/dashboard" },
        {
          text: "Insights de Pedidos",
          icon: <Psychology />,
          path: "/dashboard/conversations",
        },
      ],
    },
    {
      title: "AI Center",
      items: [
        { text: "Chats", icon: <Forum />, path: "/dashboard/chats" },
      ],
    },
    {
      title: "Mi agencia de viajes",
      items: [
        {
          text: "Gestión de viajes",
          icon: <LocationOn />,
          path: "/dashboard/travel/locations",
          activePaths: [
            "/dashboard/travel/locations",
            "/dashboard/travel/services",
            "/dashboard/travel/packages",
            "/dashboard/travel/bookings",
          ],
        },
        {
          text: "Mi agencia de viajes",
          icon: <Luggage />,
          path: "/dashboard/travel/settings",
          activePaths: [
            "/dashboard/travel",
            "/dashboard/travel/settings",
            "/dashboard/travel/design",
            "/dashboard/travel/pages",
            "/dashboard/travel/seo",
            "/dashboard/travel/domains",
          ],
        },
      ],
    },
    {
      title: "Mi renta de carros",
      items: [
        {
          text: "Mi renta de carros",
          icon: <DirectionsCar />,
          path: "/dashboard/car-rental/fleet",
          activePaths: [
            "/dashboard/car-rental",
            "/dashboard/car-rental/settings",
            "/dashboard/car-rental/design",
            "/dashboard/car-rental/pages",
            "/dashboard/car-rental/seo",
            "/dashboard/car-rental/fleet",
            "/dashboard/car-rental/domains",
          ],
        },
        {
          text: "Reservas de carros",
          icon: <DirectionsCar />,
          path: "/dashboard/car-rental/reservations",
        },
      ],
    },
    {
      title: "Mis hospedajes",
      items: [
        {
          text: "Mis hospedajes",
          icon: <Cabin />,
          path: "/dashboard/stays/properties",
          activePaths: [
            "/dashboard/stays",
            "/dashboard/stays/settings",
            "/dashboard/stays/design",
            "/dashboard/stays/pages",
            "/dashboard/stays/seo",
            "/dashboard/stays/properties",
            "/dashboard/stays/domains",
          ],
        },
        {
          text: "Reservas de hospedaje",
          icon: <Cabin />,
          path: "/dashboard/stays/reservations",
        },
      ],
    },
    {
      title: "Facturación",
      items: [
        {
          text: "Ventas y cartera",
          icon: <Insights />,
          path: "/dashboard/invoices/sales",
          activePaths: [
            "/dashboard/invoices/analytics",
            "/dashboard/invoices/new-sale",
            "/dashboard/invoices/sales",
          ],
        },
        {
          text: "Catálogo",
          icon: <Inventory2 />,
          path: "/dashboard/invoices/items",
          activePaths: [
            "/dashboard/invoices/categories",
            "/dashboard/invoices/items",
          ],
        },
        {
          text: "Cotizaciones",
          icon: <RequestQuote />,
          path: "/dashboard/invoices/quotes",
          activePaths: [
            "/dashboard/invoices/quotes",
            "/dashboard/invoices/quote-history",
          ],
        },
        {
          text: "Clientes y vendedores",
          icon: <Group />,
          path: "/dashboard/invoices/customers",
          activePaths: [
            "/dashboard/invoices/customers",
            "/dashboard/invoices/sellers",
          ],
        },
        {
          text: "Recordatorios",
          icon: <EventRepeat />,
          path: "/dashboard/invoices/reminders",
        },
      ],
    },
    {
      title: "Configuración",
      items: [
        {
          text: "Configuración del negocio",
          icon: <Settings />,
          path: "/dashboard/settings",
        },
        {
          text: "WhatsApp Cloud API",
          icon: <WhatsApp />,
          path: "/dashboard/meta-cloud-api",
          accessPath: "/dashboard/settings",
        },
        {
          text: "Mi Plan Lypro",
          icon: <WorkspacePremium />,
          path: "/dashboard/billing",
        },
        {
          text: "Lypro Admin",
          icon: <AdminPanelSettings />,
          path: "/dashboard/admin",
        },
      ],
    },
  ];

  const menuNotifications = (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          mt: 1.5,
          minWidth: 320,
          maxWidth: 360,
          borderRadius: 3,
          boxShadow: 4,
          p: 0,
        },
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", alignItems: "center" }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 700 }}>
          Notificaciones
        </Typography>
      </Box>
      <Divider />

      {notifications.length === 0 ? (
        <MenuItem
          disabled
          sx={{
            py: 3,
            justifyContent: "center",
            color: "text.secondary",
            opacity: 0.8,
            fontSize: 15,
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
          >
            <Typography sx={{ color: "text.secondary" }}>
              No tienes notificaciones
            </Typography>
          </Box>
        </MenuItem>
      ) : (
        notifications.map((n) => (
          <MenuItem
            key={n.id}
            sx={{ alignItems: "flex-start", gap: 1.25, py: 1, px: 1.7 }}
            onClick={handleClose}
          >
            <ListItemAvatar sx={{ minWidth: 26 }}>
              <Avatar
                sx={{ bgcolor: n.color, width: 26, height: 26, fontSize: 13.5 }}
              >
                {n.icon}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {n.title}
                </Typography>
              }
              secondary={
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {n.subtitle}
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      float: "right",
                      color: "text.disabled",
                      fontWeight: 400,
                      ml: 2,
                    }}
                  >
                    {n.time}
                  </Typography>
                </Box>
              }
              sx={{ my: 0 }}
            />
          </MenuItem>
        ))
      )}
    </Menu>
  );

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <List sx={{ flexGrow: 1, fontSize: 8, py: 0.5 }}>
        {soulLoading ? (
          <Box sx={{ px: 1.7, pt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minHeight: 31,
                color: "text.secondary",
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  width: 13,
                  height: 13,
                  borderRadius: "50%",
                  border: "2px solid",
                  borderColor: "divider",
                  borderTopColor: "primary.main",
                  animation: "sidebarAccessSpin 1s linear infinite",
                  "@keyframes sidebarAccessSpin": {
                    from: { transform: "rotate(0deg)" },
                    to: { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Typography variant="caption" sx={{ fontSize: 10.5 }}>
                Cargando accesos...
              </Typography>
            </Box>
          </Box>
        ) : menuGroups.map((group, idx) => {
          const visibleItems = group.items.filter(({ path, accessPath }) =>
            permittedPaths.includes(accessPath || path)
          );
          if (visibleItems.length === 0) return null;
          return (
            <Box key={group.title} sx={{ mb: 1.7 }}>
              <Typography
                variant="caption"
                sx={{
                  px: 1.7,
                  pt: idx === 0 ? 0 : 2.55,
                  pb: 0.5,
                  color: "text.secondary",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.85,
                  fontSize: "9.5px",
                }}
              >
                {group.title}
              </Typography>
              {group.items
                .filter(({ path, accessPath }) =>
                  permittedPaths.includes(accessPath || path)
                )
                .map(({ text, icon, path, activePaths }) => {
                  const isSelected = (activePaths || [path]).includes(
                    location.pathname
                  );

                  return (
                    <ListItemButton
                      key={text}
                      component={Link}
                      to={path}
                      onClick={isMobile ? () => setMobileOpen(false) : undefined}
                      selected={isSelected}
                      sx={{
                        borderRadius: "0px",
                        height: "31px",
                        bgcolor: isSelected ? "action.selected" : "transparent",
                        margin: "2px 2px",
                        padding: "7px 10px",
                        borderRight: `3px solid ${
                          isSelected ? theme.palette.primary.main : "transparent"
                        }`,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 27 }}>
                        {React.cloneElement(icon, {
                          sx: {
                            fontSize: 15,
                            color: "text.primary",
                          },
                        })}
                      </ListItemIcon>
                      <ListItemText
                        primary={text}
                        slotProps={{
                          primary: {
                            sx: {
                              fontSize: "11px",
                              fontWeight: "normal",
                              color: "text.primary",
                            },
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
            </Box>
          );
        })}
      </List>
      <Divider />
      <Box sx={{ p: 1.15, display: "flex", flexDirection: "column", gap: 0.9 }}>
        <Button
          component="a"
          href={supportWhatsAppUrl}
          target="_blank"
          rel="noreferrer"
          fullWidth
          startIcon={<WhatsApp sx={{ fontSize: 15 }} />}
          sx={{
            minHeight: 40,
            justifyContent: "flex-start",
            borderRadius: 1.75,
            px: 1.1,
            color: "#25D366",
            bgcolor:
              theme.palette.mode === "light"
                ? "rgba(37, 211, 102, 0.10)"
                : "rgba(37, 211, 102, 0.12)",
            border: "1px solid rgba(37, 211, 102, 0.28)",
            textTransform: "none",
            fontSize: 10.6,
            fontWeight: 800,
            lineHeight: 1.35,
            "& .MuiButton-startIcon": { mr: 0.7 },
            "&:hover": {
              bgcolor:
                theme.palette.mode === "light"
                  ? "rgba(37, 211, 102, 0.16)"
                  : "rgba(37, 211, 102, 0.20)",
            },
          }}
        >
          ¿Necesitas ayuda con LYPRO?
        </Button>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.55,
            p: 0.95,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor:
              theme.palette.mode === "light"
                ? "rgba(20, 24, 36, 0.035)"
                : "rgba(255, 255, 255, 0.055)",
          }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "34px minmax(0, 1fr) 31px", alignItems: "center", gap: 0.8 }}>
            <Avatar
              src=""
              alt={userData?.fullName || "Usuario"}
              sx={{ width: 34, height: 34, flexShrink: 0 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body1"
                noWrap
                sx={{ fontWeight: 900, fontSize: 10.9, lineHeight: 1.15 }}
              >
                {userData?.fullName || "Usuario LYPRO"}
              </Typography>
              <Typography
                variant="body2"
                noWrap
                sx={{ color: "text.secondary", fontSize: 9.2, mt: 0.25 }}
              >
                {user?.email || user?.phoneNumber || "Sesión activa"}
              </Typography>
            </Box>
            <Tooltip title="Preferencias">
              <IconButton
                onClick={handleProfileMenuOpen}
                aria-label="Abrir preferencias"
                aria-controls={profileMenuOpen ? "profile-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={profileMenuOpen ? "true" : undefined}
                sx={{
                  width: 31,
                  height: 31,
                  flexShrink: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor:
                    theme.palette.mode === "light"
                      ? "rgba(20, 24, 36, 0.04)"
                      : "rgba(255, 255, 255, 0.06)",
                  color: "text.primary",
                  "&:hover": {
                    bgcolor:
                      theme.palette.mode === "light"
                        ? "rgba(20, 24, 36, 0.08)"
                        : "rgba(255, 255, 255, 0.12)",
                  },
                }}
              >
                <Settings sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0 }}>
            <Typography
              component="span"
              noWrap
              sx={{
                px: 0.55,
                py: 0.15,
                borderRadius: 999,
                bgcolor: "rgba(138, 0, 204, 0.14)",
                color: theme.palette.mode === "light" ? "primary.main" : "#E6B8FF",
                fontSize: 8.6,
                fontWeight: 900,
                lineHeight: 1.2,
                textTransform: "uppercase",
              }}
            >
              {roleLabel}
            </Typography>
            <Typography
              component="span"
              sx={{ color: "text.secondary", fontSize: 8.8, flexShrink: 0 }}
            >
              v{appVersion}
            </Typography>
          </Box>
        </Box>
        <Menu
          id="profile-menu"
          anchorEl={profileMenuAnchorEl}
          open={profileMenuOpen}
          onClose={handleProfileMenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "right" }}
          PaperProps={{
            sx: {
              mb: 1,
              minWidth: 187,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: 4,
            },
          }}
        >
          <MenuItem onClick={handleThemeMenuClick}>
            <ListItemIcon>
              {isDarkMode ? (
                <LightMode fontSize="small" />
              ) : (
                <DarkMode fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={isDarkMode ? "Cambiar a tema claro" : "Cambiar a tema oscuro"}
              slotProps={{ primary: { sx: { fontSize: 11 } } }}
            />
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogoutMenuClick} sx={{ color: "error.main" }}>
            <ListItemIcon sx={{ color: "error.main" }}>
              <Logout fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Cerrar sesión"
              slotProps={{ primary: { sx: { fontSize: 11 } } }}
            />
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        height: "100dvh",
        minHeight: "100dvh",
        overflow: "hidden",
      }}
    >
      <ConfirmationDialog
        open={dialogOpen}
        title="Confirmar cierre de sesión"
        message="¿Estás seguro de que deseas cerrar sesión?"
        onClose={() => setDialogOpen(false)}
        onConfirm={handleLogout}
        confirmText="Cerrar sesión"
        cancelText="Cancelar"
      />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { xs: "flex", md: "none" },
          width: "100%",
          ml: 0,
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar
          sx={{
            minHeight: "52px !important",
            px: 1.25,
          }}
        >
          {isMobile && (
            <IconButton
              edge="start"
              onClick={handleDrawerToggle}
              aria-label="Abrir menú"
              sx={{
                width: 36,
                height: 36,
                mr: 1.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor:
                  theme.palette.mode === "light"
                    ? "rgba(20, 24, 36, 0.04)"
                    : "rgba(255, 255, 255, 0.06)",
                color: "text.primary",
                "&:hover": {
                  bgcolor:
                    theme.palette.mode === "light"
                      ? "rgba(20, 24, 36, 0.08)"
                      : "rgba(255, 255, 255, 0.12)",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      {menuNotifications}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            zIndex: (theme) => theme.zIndex.drawer,
          },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 1.4,
            py: 2.1,
            minHeight: 78,
            bgcolor:
              theme.palette.mode === "light"
                ? "#ffffff"
                : "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
            boxShadow: "none",
            mb: 1,
            "&::after": {
              content: '""',
              position: "absolute",
              left: 28,
              right: 28,
              bottom: 0,
              height: 2,
              borderRadius: 999,
              background:
                "linear-gradient(90deg, rgba(138,0,204,0), rgba(138,0,204,0.82), rgba(57,255,20,0.58), rgba(138,0,204,0))",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "grid",
              placeItems: "center",
              width: "100%",
              maxWidth: 126,
            }}
          >
            <img
              src="/assets/icons/ic-logo-light.png"
              alt="Logo"
              style={{ height: 42, display: "block" }}
            />
          </Box>
        </Box>
        {drawerContent}
      </Drawer>
        <Box
          component="main"
          sx={{
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            minHeight: 0,
            height: "100dvh",
            width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
            bgcolor: "background.default",
            p: 0,
            overflowX: "hidden",
            position: "relative",
            zIndex: 0,
          }}
        >
        <Toolbar
          sx={{
            display: { xs: "flex", md: "none" },
            flexShrink: 0,
            minHeight: "52px !important",
          }}
        />
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
